"use client";

import { useContext } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { FormFunnelBannerImage } from "@portal/app/_components/formFunnel/FormFunnelBannerImage";
import { FormFunnelHeadingContext } from "@portal/app/_components/formFunnel/FormFunnelHeadingContext";
import { SANS_FONT_STACK } from "@portal/app/_components/theme/options/typography";
import { PageFlowContext } from "../puck/PageFlowContext";
import type { PageOwnedFormOptions } from "../puck/types";
import type { PageMedia } from "../types";

/** Presentation around the current screen never owns questionnaire state. */
export function LandingPageFormScreen({
  options,
  media,
}: {
  options: PageOwnedFormOptions;
  media: PageMedia[];
}) {
  const flow = useContext(PageFlowContext);
  if (!flow) return null;
  const firstQuestion = flow.flowState === "questions" && flow.isFirstQuestionStep;
  const introWithinScreen = options.stepLayout === "step" || options.stepLayout === "after-banner";
  const banner = firstQuestion ? media.find((image) => image.id === options.bannerMediaId) : null;
  const showIntro =
    firstQuestion &&
    !introWithinScreen &&
    Boolean(options.introTitle?.trim() || options.introSubtitle?.trim());
  const hasIntroHeading = firstQuestion && Boolean(options.introTitle?.trim());
  const introductionHeading = flow.pageHasHeading ? "h2" : "h1";
  const screenHeading =
    flow.pageHasHeading || (!introWithinScreen && hasIntroHeading) ? "h2" : "h1";
  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        mt:
          flow.flowState === "questions" &&
          (options.stepLayout === "step" ||
            (options.stepLayout === "after-banner" && !firstQuestion))
            ? 4
            : 0,
        ...(options.stepLayout === "after-banner" && firstQuestion
          ? { "@media (min-width: 800px)": { mt: 4 } }
          : {}),
        ...(options.questionTypography === "body"
          ? { "& [data-funnel-question-label]": { fontFamily: SANS_FONT_STACK, fontWeight: 600 } }
          : {}),
      }}
    >
      {banner && <FormFunnelBannerImage image={banner} />}
      {showIntro && (
        <Box
          data-testid="embedded-form-intro"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "12px",
            mt: "8px",
            mb: "26px",
            textAlign: "center",
          }}
        >
          {options.introTitle?.trim() && (
            <Typography
              component={introductionHeading}
              sx={{
                m: 0,
                fontFamily: SANS_FONT_STACK,
                fontSize: "1.75rem",
                fontWeight: 700,
                lineHeight: 1.15,
                letterSpacing: "-0.01em",
                color: "primary.main",
              }}
            >
              {options.introTitle}
            </Typography>
          )}
          {options.introSubtitle && (
            <Typography
              component="p"
              sx={{
                m: 0,
                fontFamily: SANS_FONT_STACK,
                fontSize: "1rem",
                lineHeight: 1.5,
                color: "dream.grayMediumDark",
              }}
            >
              {options.introSubtitle}
            </Typography>
          )}
        </Box>
      )}
      <FormFunnelHeadingContext.Provider value={screenHeading}>
        {flow.screen}
      </FormFunnelHeadingContext.Provider>
    </Box>
  );
}
